import {
  ArrowLeft,
  Check,
  ChevronDown,
  Copy,
  Download,
  ExternalLink,
  FileUp,
  Library,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Sparkles,
  Tags,
  Trash2,
  X,
} from "lucide-react";
import { type FormEvent, type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Alert, AlertDescription, AlertTitle } from "./components/ui/alert";
import { Badge } from "./components/ui/badge";
import { Button } from "./components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./components/ui/card";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Select } from "./components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./components/ui/table";
import { Textarea } from "./components/ui/textarea";
import { AdminTag, AiAnalysis, apiGet, apiSend, Paper, PaperInput, SourceArtifact, TagAlias, TagType } from "./lib/api";
import {
  evidenceTypeOptions,
  exposurePathwayOptions,
  populationOptions,
  policyTagOptions,
  sourceTypeOptions,
  topicOptions
} from "./lib/taxonomy";
import { cn, formatAuthors, formatShortAuthors, normalizeUrl } from "./lib/utils";

type View = "library" | "manage" | "tags" | "intake" | "admin";
type SortKey = "year" | "title";
type CitationFormat = "chicago" | "apa" | "mla" | "bibtex";
type EditablePaper = PaperInput & { id?: string };

const citationFormatOptions: Array<{ value: CitationFormat; label: string }> = [
  { value: "chicago", label: "Chicago" },
  { value: "apa", label: "APA" },
  { value: "mla", label: "MLA" },
  { value: "bibtex", label: "BibTeX" }
];

const emptyPaper: EditablePaper = {
  title: "",
  authors: [],
  year: null,
  publication: "",
  sourceType: "journal",
  doi: "",
  url: "",
  abstract: "",
  geography: "",
  geographyTags: [],
  geographicScope: "unknown",
  countries: [],
  region: "",
  topics: [],
  policyTags: [],
  populationTags: [],
  exposureTags: [],
  evidenceType: "cross-sectional study",
  relevanceScore: 75,
  reviewStatus: "approved",
  coreQuestion: "",
  keyFinding: "",
  summary: "",
  decisionRelevance: "",
  notes: ""
};

export default function App() {
  const [path, setPath] = useState(() => window.location.pathname);
  const [papers, setPapers] = useState<Paper[]>([]);
  const [selectedId, setSelectedId] = useState("");
  const [editingPaper, setEditingPaper] = useState<EditablePaper>(emptyPaper);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("year");
  const [filters, setFilters] = useState({
    search: "",
    population: "all",
    exposure: "all",
    policy: "all",
    geography: "all"
  });
  const view: View = path.startsWith("/admin")
    ? "admin"
    : path.startsWith("/manage/tags")
      ? "tags"
    : path.startsWith("/manage")
      ? "manage"
      : path.startsWith("/intake")
        ? "intake"
        : "library";
  const paperPageId = path.startsWith("/papers/") ? decodeURIComponent(path.slice("/papers/".length)) : "";

  function navigate(to: string) {
    window.history.pushState(null, "", to);
    setPath(to);
    window.scrollTo({ top: 0 });
  }

  async function loadData() {
    setLoading(true);
    setError("");
    try {
      const paperResponse = await apiGet<{ papers: Paper[] }>("/api/papers");
      setPapers(paperResponse.papers);
      setSelectedId((current) => current || paperResponse.papers[0]?.id || "");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to load research entries");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadData();
  }, []);

  useEffect(() => {
    function syncPath() {
      setPath(window.location.pathname);
    }

    window.addEventListener("popstate", syncPath);
    return () => window.removeEventListener("popstate", syncPath);
  }, []);

  const geographies = useMemo(() => {
    const values = new Set<string>();
    for (const paper of papers) {
      geographyTagsForPaper(paper).forEach((tag) => values.add(tag));
    }
    return Array.from(values).sort();
  }, [papers]);
  const populations = useMemo(() => facetOptions(papers.flatMap((paper) => paper.populationTags)), [papers]);
  const exposures = useMemo(() => facetOptions(papers.flatMap((paper) => paper.exposureTags)), [papers]);
  const policies = useMemo(() => facetOptions(papers.flatMap((paper) => paper.policyTags)), [papers]);

  const filteredPapers = useMemo(() => {
    const search = filters.search.trim().toLowerCase();
    const filtered = papers.filter((paper) => {
      const matchesSearch =
        !search ||
        [
          paper.title,
          paper.publication,
          paper.summary,
          paper.coreQuestion,
          paper.keyFinding,
          paper.abstract,
          paper.decisionRelevance,
          paper.geography,
          geographyTagsForPaper(paper).join(" "),
          paper.doi,
          paper.populationTags.join(" "),
          paper.exposureTags.join(" "),
          paper.policyTags.join(" "),
          paper.authors.join(" ")
        ]
          .join(" ")
          .toLowerCase()
          .includes(search);

      const matchesPopulation = filters.population === "all" || paper.populationTags.includes(filters.population);
      const matchesExposure = filters.exposure === "all" || paper.exposureTags.includes(filters.exposure);
      const matchesPolicy = filters.policy === "all" || paper.policyTags.includes(filters.policy);
      const matchesGeo = filters.geography === "all" || geographyTagsForPaper(paper).includes(filters.geography);

      return matchesSearch && matchesPopulation && matchesExposure && matchesPolicy && matchesGeo;
    });

    return filtered.sort((a, b) => {
      if (sortKey === "title") return a.title.localeCompare(b.title);
      return (b.year ?? 0) - (a.year ?? 0) || a.title.localeCompare(b.title);
    });
  }, [filters, papers, sortKey]);

  const paperPage = useMemo(() => {
    return paperPageId ? papers.find((paper) => paper.id === paperPageId) : undefined;
  }, [paperPageId, papers]);

  async function savePaper(paper: EditablePaper) {
    setSaving(true);
    setError("");
    try {
      const response = paper.id
        ? await apiSend<{ paper: Paper }>(`/api/admin/papers/${paper.id}`, "PUT", paper)
        : await apiSend<{ paper: Paper }>("/api/admin/papers", "POST", paper);

      setSelectedId(response.paper.id);
      setEditingPaper(emptyPaper);
      await loadData();
      navigate(view === "manage" ? "/manage" : `/papers/${response.paper.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to save entry");
    } finally {
      setSaving(false);
    }
  }

  async function deletePaper(paper: Paper) {
    if (!window.confirm(`Delete "${paper.title}"?`)) return;
    setSaving(true);
    setError("");
    try {
      await apiSend<{ ok: boolean }>(`/api/admin/papers/${paper.id}`, "DELETE");
      setSelectedId("");
      await loadData();
      navigate("/manage");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to delete entry");
    } finally {
      setSaving(false);
    }
  }

  function editPaper(paper: Paper) {
    navigate("/manage");
    setEditingPaper(toEditable(paper));
    setSelectedId(paper.id);
  }

  function newPaper() {
    setSelectedId("new");
    navigate("/manage");
    setEditingPaper(emptyPaper);
  }

  return (
    <div className="min-h-screen">
      <AppHeader view={view} navigate={navigate} onNew={newPaper} />

      <main className="container py-4 md:py-6">
        {!paperPageId && view === "library" ? (
          <section className="mb-4 border-b pb-4 md:mb-5">
            <div className="grid gap-1.5">
              <h1 className="research-title text-[29px] font-semibold leading-8 tracking-normal md:text-[40px] md:leading-[46px]">
                Lead Battery Research Database
              </h1>
              <a
                className="inline-flex w-fit items-center gap-1 text-sm font-medium text-muted-foreground underline underline-offset-4 hover:text-foreground"
                href="https://leadbatteries.org"
                rel="noreferrer"
                target="_blank"
              >
                A project of LeadBatteries.org
                <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
              </a>
            </div>
          </section>
        ) : null}

        {error ? (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}

        {!paperPageId && view === "library" ? (
          <DatabaseToolbar
            filters={filters}
            exposures={exposures}
            geographies={geographies}
            loading={loading}
            policies={policies}
            populations={populations}
            resultCount={filteredPapers.length}
            setFilters={setFilters}
            setSortKey={setSortKey}
            sortKey={sortKey}
            onRefresh={loadData}
          />
        ) : null}

        {paperPageId ? (
          <PaperPage paper={paperPage} loading={loading} onBack={() => navigate("/")} onEdit={editPaper} />
        ) : view === "library" ? (
          <LibraryView
            filteredPapers={filteredPapers}
            onOpen={(paper) => {
              setSelectedId(paper.id);
              navigate(`/papers/${paper.id}`);
            }}
          />
        ) : view === "intake" ? (
          <IntakeView />
        ) : view === "admin" ? (
          <AdminReviewQueue
            onManageEntries={() => navigate("/manage")}
            onPublished={async (paperId) => {
              await loadData();
              navigate(`/papers/${paperId}`);
            }}
          />
        ) : view === "tags" ? (
          <TagManagerView
            papers={papers}
            onManageEntries={() => navigate("/manage")}
            onOpenEntry={(paper) => {
              setSelectedId(paper.id);
              navigate(`/papers/${paper.id}`);
            }}
            onTagsChanged={loadData}
          />
        ) : (
          <ManageView
            papers={filteredPapers}
            selectedId={selectedId}
            setSelectedId={setSelectedId}
            editingPaper={editingPaper}
            setEditingPaper={setEditingPaper}
            saving={saving}
            onSave={savePaper}
            onEdit={(paper) => {
              setSelectedId(paper.id);
              setEditingPaper(toEditable(paper));
            }}
            onNew={newPaper}
            onDelete={deletePaper}
            onManageTags={() => navigate("/manage/tags")}
          />
        )}
      </main>
    </div>
  );
}

function AppHeader({ view, navigate, onNew }: { view: View; navigate: (to: string) => void; onNew: () => void }) {
  return (
    <header className="sticky top-0 z-30 border-b bg-card">
      <div className="container flex min-h-14 items-center justify-between gap-4 py-2">
        <button className="flex min-w-0 items-center gap-2.5 text-left" onClick={() => navigate("/")} type="button">
          <span className="truncate text-base text-muted-foreground">
            <span className="font-semibold text-foreground">LBR</span> Database
          </span>
        </button>
        <nav className="flex items-center gap-2">
          <Button variant={view === "library" ? "default" : "ghost"} size="sm" onClick={() => navigate("/")}>
            <Library className="h-4 w-4" aria-hidden="true" />
            Library
          </Button>
          <Button variant={view === "intake" ? "default" : "ghost"} size="sm" onClick={() => navigate("/intake")}>
            <FileUp className="h-4 w-4" aria-hidden="true" />
            Submit source
          </Button>
          <a
            className="hidden h-8 items-center justify-center gap-2 rounded-sm px-3 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring md:inline-flex"
            href="https://leadbatteries.org"
            rel="noreferrer"
            target="_blank"
          >
            LeadBatteries.org
            <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
          </a>
        </nav>
      </div>
    </header>
  );
}

function DatabaseToolbar({
  filters,
  setFilters,
  geographies,
  populations,
  exposures,
  policies,
  sortKey,
  setSortKey,
  loading,
  resultCount,
  onRefresh
}: {
  filters: {
    search: string;
    population: string;
    exposure: string;
    policy: string;
    geography: string;
  };
  setFilters: (filters: {
    search: string;
    population: string;
    exposure: string;
    policy: string;
    geography: string;
  }) => void;
  geographies: string[];
  populations: string[];
  exposures: string[];
  policies: string[];
  sortKey: SortKey;
  setSortKey: (sortKey: SortKey) => void;
  loading: boolean;
  resultCount: number;
  onRefresh: () => Promise<void>;
}) {
  return (
    <section className="index-surface mb-4 border bg-card p-3 lg:sticky lg:top-14 lg:z-20">
      <div className="grid gap-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-baseline gap-2">
            <span className="font-mono text-base font-semibold">{loading ? "Loading" : resultCount.toLocaleString()}</span>
            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {loading ? "entries" : resultCount === 1 ? "entry" : "entries"}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={onRefresh}>
              <RefreshCw className="h-4 w-4" aria-hidden="true" />
              Refresh
            </Button>
            <Button variant="outline" size="sm" onClick={() => (window.location.href = "/api/export.csv")}>
              <Download className="h-4 w-4" aria-hidden="true" />
              CSV
            </Button>
          </div>
        </div>
        <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-[minmax(260px,1fr)_180px_165px_185px_185px_145px]">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              aria-label="Search entries"
              className="pl-9"
              placeholder="Search title, author, DOI, location..."
              value={filters.search}
              onChange={(event) => setFilters({ ...filters, search: event.target.value })}
            />
          </div>
          <Select
            aria-label="Filter by geography"
            value={filters.geography}
            onChange={(event) => setFilters({ ...filters, geography: event.target.value })}
          >
            <option value="all">All geographies</option>
            {geographies.map((geography) => (
              <option value={geography} key={geography}>
                {geography}
              </option>
            ))}
          </Select>
          <Select
            aria-label="Filter by population"
            value={filters.population}
            onChange={(event) => setFilters({ ...filters, population: event.target.value })}
          >
            <option value="all">All populations</option>
            {populations.map((population) => (
              <option value={population} key={population}>
                {population}
              </option>
            ))}
          </Select>
          <Select
            aria-label="Filter by exposure pathway"
            value={filters.exposure}
            onChange={(event) => setFilters({ ...filters, exposure: event.target.value })}
          >
            <option value="all">All exposures</option>
            {exposures.map((exposure) => (
              <option value={exposure} key={exposure}>
                {exposure}
              </option>
            ))}
          </Select>
          <Select
            aria-label="Filter by policy lever"
            value={filters.policy}
            onChange={(event) => setFilters({ ...filters, policy: event.target.value })}
          >
            <option value="all">All policy levers</option>
            {policies.map((policy) => (
              <option value={policy} key={policy}>
                {policy}
              </option>
            ))}
          </Select>
          <Select
            aria-label="Sort entries"
            value={sortKey}
            onChange={(event) => setSortKey(event.target.value as SortKey)}
          >
            <option value="year">Sort: newest</option>
            <option value="title">Sort: title</option>
          </Select>
        </div>
      </div>
    </section>
  );
}

function LibraryView({
  filteredPapers,
  onOpen
}: {
  filteredPapers: Paper[];
  onOpen: (paper: Paper) => void;
}) {
  return (
    <section>
      <PapersTable papers={filteredPapers} onOpen={onOpen} />
    </section>
  );
}

function ManageView({
  papers,
  selectedId,
  setSelectedId,
  editingPaper,
  setEditingPaper,
  saving,
  onSave,
  onEdit,
  onNew,
  onDelete,
  onManageTags
}: {
  papers: Paper[];
  selectedId: string;
  setSelectedId: (id: string) => void;
  editingPaper: EditablePaper;
  setEditingPaper: (paper: EditablePaper) => void;
  saving: boolean;
  onSave: (paper: EditablePaper) => Promise<void>;
  onEdit: (paper: Paper) => void;
  onNew: () => void;
  onDelete: (paper: Paper) => Promise<void>;
  onManageTags: () => void;
}) {
  const [adminToken, setAdminToken] = useState(() => window.localStorage.getItem("lbr_admin_token") || "");
  const [tokenInput, setTokenInput] = useState(adminToken);
  const [requiresToken, setRequiresToken] = useState(true);
  const [localBypass, setLocalBypass] = useState(false);
  const [authMode, setAuthMode] = useState("");
  const [adminVerified, setAdminVerified] = useState(false);
  const [adminError, setAdminError] = useState("");
  const editingActive = selectedId === "new" || Boolean(editingPaper.id);
  const closeEditor = () => {
    setSelectedId("");
    setEditingPaper(emptyPaper);
  };

  useEffect(() => {
    let active = true;

    async function verifyAdminToken() {
      try {
        const status = await apiGet<{
          authMode: string;
          localBypass: boolean;
          requiresToken: boolean;
        }>("/api/admin/status");
        if (!active) return;

        setAuthMode(status.authMode);
        setLocalBypass(status.localBypass);
        setRequiresToken(status.requiresToken);

        if (!status.requiresToken) {
          setAdminVerified(status.authMode !== "unconfigured");
          setAdminError(status.authMode === "unconfigured" ? "Admin authorization is not configured." : "");
          return;
        }
      } catch {
        if (!active) return;
        setAdminVerified(false);
        setAdminError("Unable to check admin authorization.");
        return;
      }

      if (!adminToken) {
        setAdminVerified(false);
        return;
      }

      try {
        const response = await fetch("/api/admin/verify", {
          headers: { authorization: `Bearer ${adminToken}` }
        });
        if (!response.ok) throw new Error(await response.text());
        if (active) {
          setAdminVerified(true);
          setAdminError("");
        }
      } catch {
        window.localStorage.removeItem("lbr_admin_token");
        if (active) {
          setAdminToken("");
          setTokenInput("");
          setAdminVerified(false);
          setAdminError("Admin token was not accepted.");
        }
      }
    }

    void verifyAdminToken();

    return () => {
      active = false;
    };
  }, [adminToken]);

  function saveAdminToken(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    window.localStorage.setItem("lbr_admin_token", tokenInput);
    setAdminVerified(false);
    setAdminError("");
    setAdminToken(tokenInput);
  }

  if (!adminVerified && !requiresToken) {
    return (
      <section className="mx-auto grid max-w-md gap-4">
        <div>
          <p className="text-sm font-medium text-muted-foreground">Admin</p>
          <h1 className="mt-1 text-3xl font-semibold tracking-normal">Manage entries</h1>
        </div>
        <Alert variant="destructive">
          <AlertDescription>{adminError || "Admin authorization is unavailable."}</AlertDescription>
        </Alert>
      </section>
    );
  }

  if (requiresToken && (!adminToken || !adminVerified)) {
    return (
      <section className="mx-auto grid max-w-md gap-4">
        <div>
          <p className="text-sm font-medium text-muted-foreground">Admin</p>
          <h1 className="mt-1 text-3xl font-semibold tracking-normal">Manage entries</h1>
        </div>
        <Card className="rounded-none">
          <form onSubmit={saveAdminToken}>
            <CardHeader className="border-b px-4 py-4">
              <CardTitle className="text-base">Admin access</CardTitle>
              <CardDescription>Enter the local admin token to create, edit, or delete database entries.</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 px-4 py-4">
              {adminError ? (
                <Alert variant="destructive">
                  <AlertDescription>{adminError}</AlertDescription>
                </Alert>
              ) : null}
              <Field label="Admin token">
                <Input
                  autoComplete="current-password"
                  onChange={(event) => setTokenInput(event.target.value)}
                  type="password"
                  value={tokenInput}
                />
              </Field>
              <Button type="submit">{adminToken && !adminVerified ? "Checking..." : "Open content manager"}</Button>
            </CardContent>
          </form>
        </Card>
      </section>
    );
  }

  return (
    <section className="grid gap-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-muted-foreground">Admin</p>
          <h1 className="mt-1 text-3xl font-semibold tracking-normal">Manage entries</h1>
          <p className="mt-2 text-sm text-muted-foreground">Create, edit, and remove published research database entries.</p>
          {localBypass ? <p className="mt-2 text-sm text-muted-foreground">Local admin bypass is active.</p> : null}
          {authMode === "access" ? <p className="mt-2 text-sm text-muted-foreground">Protected by Cloudflare Access.</p> : null}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={onManageTags}>
            <Tags className="h-4 w-4" aria-hidden="true" />
            Manage tags
          </Button>
          <Button onClick={onNew}>
            <Plus className="h-4 w-4" aria-hidden="true" />
            New entry
          </Button>
        </div>
      </div>

      <Card className="overflow-hidden rounded-none">
        <CardHeader className="border-b px-4 py-3">
          <div>
            <CardTitle className="text-base">Database entries</CardTitle>
            <CardDescription>{papers.length} entries available for admin editing.</CardDescription>
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto p-0">
          <Table className="min-w-[980px]">
            <TableHeader className="bg-muted/70 text-xs uppercase tracking-wide">
              <TableRow>
                <TableHead className="w-[44%]">Entry</TableHead>
                <TableHead className="w-[8%] text-center">Year</TableHead>
                <TableHead className="w-[16%]">Geography</TableHead>
                <TableHead className="w-[14%]">Type</TableHead>
                <TableHead className="w-[12%]">Updated</TableHead>
                <TableHead className="w-[6%] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {papers.map((paper) => (
                <TableRow className={cn(selectedId === paper.id && "bg-muted/60")} key={paper.id}>
                  <TableCell>
                    <button
                      className="block min-w-0 text-left"
                      onClick={() => {
                        setSelectedId(paper.id);
                        onEdit(paper);
                      }}
                      type="button"
                    >
                      <span className="line-clamp-2 text-sm font-semibold leading-5">{paper.title}</span>
                      <span className="mt-1 block truncate text-xs text-muted-foreground">
                        {[formatAuthors(paper.authors), paper.publication].filter(Boolean).join(" | ")}
                      </span>
                    </button>
                  </TableCell>
                  <TableCell className="text-center text-sm text-muted-foreground">{paper.year || "—"}</TableCell>
                  <TableCell>
                    <GeographyBadges paper={paper} compact />
                  </TableCell>
                  <TableCell>
                    <div className="grid gap-1 text-sm">
                      <span>{toTitleCase(paper.evidenceType || "Unknown")}</span>
                      <span className="text-xs text-muted-foreground">{toTitleCase(paper.sourceType || "Unknown")}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{formatTimestamp(paper.updatedAt) || "—"}</TableCell>
                  <TableCell>
                    <div className="flex justify-end gap-1">
                      <IconButton
                        label="Edit"
                        onClick={() => {
                          setSelectedId(paper.id);
                          onEdit(paper);
                        }}
                      >
                        <Pencil className="h-4 w-4" aria-hidden="true" />
                      </IconButton>
                      <IconButton destructive label="Delete" onClick={() => onDelete(paper)}>
                        <Trash2 className="h-4 w-4" aria-hidden="true" />
                      </IconButton>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <EntryEditorModal open={editingActive} onClose={closeEditor}>
        <PaperEditor
          paper={editingPaper}
          setPaper={setEditingPaper}
          saving={saving}
          onCancel={closeEditor}
          onSave={onSave}
        />
      </EntryEditorModal>
    </section>
  );
}

type TagAction = {
  kind: "rename" | "merge" | "split" | "alias";
  tag: AdminTag;
};

const tagTypeLabels: Record<TagType, string> = {
  geography: "Geography",
  topic: "Topic",
  policy_lever: "Policy lever",
  population: "Population",
  exposure: "Exposure",
  evidence_type: "Evidence type"
};

const tagTypeOrder: TagType[] = ["geography", "population", "exposure", "policy_lever", "topic", "evidence_type"];

function TagManagerView({
  papers,
  onManageEntries,
  onOpenEntry,
  onTagsChanged
}: {
  papers: Paper[];
  onManageEntries: () => void;
  onOpenEntry: (paper: Paper) => void;
  onTagsChanged: () => Promise<void>;
}) {
  const [adminToken, setAdminToken] = useState(() => window.localStorage.getItem("lbr_admin_token") || "");
  const [tokenInput, setTokenInput] = useState(adminToken);
  const [requiresToken, setRequiresToken] = useState(true);
  const [tags, setTags] = useState<AdminTag[]>([]);
  const [loadingTags, setLoadingTags] = useState(true);
  const [tagError, setTagError] = useState("");
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<TagType | "all">("all");
  const [action, setAction] = useState<TagAction | null>(null);
  const [entriesTag, setEntriesTag] = useState<AdminTag | null>(null);
  const [savingAction, setSavingAction] = useState(false);

  async function adminRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
    const headers = new Headers(init.headers);
    if (adminToken) headers.set("authorization", `Bearer ${adminToken}`);
    if (init.body && !headers.has("content-type")) headers.set("content-type", "application/json");

    const response = await fetch(path, { ...init, headers });
    if (response.status === 401) window.localStorage.removeItem("lbr_admin_token");
    if (!response.ok) throw new Error(await response.text());
    return response.json() as Promise<T>;
  }

  async function loadTags() {
    setLoadingTags(true);
    setTagError("");

    try {
      const status = await apiGet<{ requiresToken: boolean; authMode: string }>("/api/admin/status");
      setRequiresToken(status.requiresToken);

      if (status.requiresToken && !adminToken) {
        setTags([]);
        return;
      }

      const response = await adminRequest<{ tags: AdminTag[] }>("/api/admin/tags");
      setTags(response.tags);
    } catch (err) {
      setTagError(err instanceof Error ? err.message : "Unable to load tags");
    } finally {
      setLoadingTags(false);
    }
  }

  useEffect(() => {
    void loadTags();
  }, [adminToken]);

  function saveAdminToken(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    window.localStorage.setItem("lbr_admin_token", tokenInput);
    setAdminToken(tokenInput);
  }

  const visibleTags = useMemo(() => {
    const query = search.trim().toLowerCase();
    return tags
      .filter((tag) => typeFilter === "all" || tag.type === typeFilter)
      .filter((tag) => {
        if (!query) return true;
        return [
          tag.label,
          tag.slug,
          tagTypeLabels[tag.type],
          ...tag.aliases.flatMap((alias) => [alias.label, alias.slug])
        ]
          .join(" ")
          .toLowerCase()
          .includes(query);
      })
      .sort((a, b) => {
        const typeDiff = tagTypeOrder.indexOf(a.type) - tagTypeOrder.indexOf(b.type);
        if (typeDiff !== 0) return typeDiff;
        return b.entryCount - a.entryCount || a.label.localeCompare(b.label);
      });
  }, [search, tags, typeFilter]);

  const entriesByTagId = useMemo(() => {
    const map = new Map<string, Paper[]>();

    for (const paper of papers) {
      for (const tag of paper.tags) {
        const entries = map.get(tag.id) || [];
        entries.push(paper);
        map.set(tag.id, entries);
      }
    }

    for (const entries of map.values()) {
      entries.sort((a, b) => (b.year || 0) - (a.year || 0) || a.title.localeCompare(b.title));
    }

    return map;
  }, [papers]);

  function entriesForTag(tag: AdminTag) {
    return entriesByTagId.get(tag.id) || [];
  }

  async function submitAction(payload: Record<string, unknown>) {
    if (!action) return;
    setSavingAction(true);
    setTagError("");

    try {
      if (action.kind === "rename") {
        await adminRequest(`/api/admin/tags/${action.tag.id}`, {
          method: "PUT",
          body: JSON.stringify({ label: payload.label })
        });
      } else if (action.kind === "merge") {
        await adminRequest(`/api/admin/tags/${action.tag.id}/merge`, {
          method: "POST",
          body: JSON.stringify({ targetTagId: payload.targetTagId })
        });
      } else if (action.kind === "split") {
        await adminRequest(`/api/admin/tags/${action.tag.id}/split`, {
          method: "POST",
          body: JSON.stringify({ labels: payload.labels })
        });
      } else if (action.kind === "alias") {
        await adminRequest(`/api/admin/tags/${action.tag.id}/aliases`, {
          method: "POST",
          body: JSON.stringify({ label: payload.label })
        });
      }

      setAction(null);
      await loadTags();
      await onTagsChanged();
    } catch (err) {
      setTagError(err instanceof Error ? err.message : "Unable to update tag");
    } finally {
      setSavingAction(false);
    }
  }

  async function deleteAlias(tag: AdminTag, alias: TagAlias) {
    setTagError("");

    try {
      await adminRequest(`/api/admin/tags/${tag.id}/aliases/${alias.id}`, {
        method: "DELETE"
      });
      await loadTags();
    } catch (err) {
      setTagError(err instanceof Error ? err.message : "Unable to delete alias");
    }
  }

  if (requiresToken && !adminToken) {
    return (
      <section className="mx-auto grid max-w-md gap-4">
        <div>
          <p className="text-sm font-medium text-muted-foreground">Admin</p>
          <h1 className="mt-1 text-3xl font-semibold tracking-normal">Manage tags</h1>
        </div>
        <Card className="rounded-none">
          <form onSubmit={saveAdminToken}>
            <CardHeader className="border-b px-4 py-4">
              <CardTitle className="text-base">Admin access</CardTitle>
              <CardDescription>Enter the admin token to clean and merge database tags.</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 px-4 py-4">
              {tagError ? (
                <Alert variant="destructive">
                  <AlertDescription>{tagError}</AlertDescription>
                </Alert>
              ) : null}
              <Field label="Admin token">
                <Input
                  autoComplete="current-password"
                  onChange={(event) => setTokenInput(event.target.value)}
                  type="password"
                  value={tokenInput}
                />
              </Field>
              <Button type="submit">Open tag manager</Button>
            </CardContent>
          </form>
        </Card>
      </section>
    );
  }

  return (
    <section className="grid gap-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-muted-foreground">Admin</p>
          <h1 className="mt-1 text-3xl font-semibold tracking-normal">Manage tags</h1>
          <p className="mt-2 text-sm text-muted-foreground">Rename, merge, split, and alias the controlled vocabulary used across entries.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={onManageEntries}>
            <Library className="h-4 w-4" aria-hidden="true" />
            Entries
          </Button>
          <Button variant="outline" onClick={() => void loadTags()}>
            <RefreshCw className="h-4 w-4" aria-hidden="true" />
            Refresh
          </Button>
        </div>
      </div>

      {tagError ? (
        <Alert variant="destructive">
          <AlertDescription>{tagError}</AlertDescription>
        </Alert>
      ) : null}

      <Card className="rounded-none">
        <CardHeader className="border-b px-4 py-3">
          <div className="grid gap-3 lg:grid-cols-[minmax(260px,1fr)_220px_160px]">
            <div>
              <CardTitle className="text-base">Controlled vocabulary</CardTitle>
              <CardDescription>
                {loadingTags ? "Loading tags" : `${visibleTags.length} of ${tags.length} tags shown`}
              </CardDescription>
            </div>
            <Select value={typeFilter} onChange={(event) => setTypeFilter(event.target.value as TagType | "all")}>
              <option value="all">All tag types</option>
              {tagTypeOrder.map((type) => (
                <option value={type} key={type}>
                  {tagTypeLabels[type]}
                </option>
              ))}
            </Select>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                aria-label="Search tags"
                className="pl-9"
                placeholder="Search tags"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto p-0">
          <Table className="min-w-[980px]">
            <TableHeader className="bg-muted/70 text-xs uppercase tracking-wide">
              <TableRow>
                <TableHead className="w-[26%]">Tag</TableHead>
                <TableHead className="w-[14%]">Type</TableHead>
                <TableHead className="w-[10%] text-right">Entries</TableHead>
                <TableHead className="w-[32%]">Aliases</TableHead>
                <TableHead className="w-[18%] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibleTags.map((tag) => (
                <TableRow key={tag.id}>
                  <TableCell>
                    <div className="grid gap-1">
                      <span className="text-sm font-semibold">{tag.label}</span>
                      <span className="font-mono text-[11px] text-muted-foreground">{tag.slug}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="rounded-sm bg-background">
                      {tagTypeLabels[tag.type]}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      className="h-8 min-w-20 justify-end gap-2 px-2 font-mono"
                      disabled={tag.entryCount === 0}
                      onClick={() => setEntriesTag(tag)}
                      size="sm"
                      variant="outline"
                    >
                      <span>{tag.entryCount}</span>
                      <span className="font-sans text-xs text-muted-foreground">View</span>
                    </Button>
                  </TableCell>
                  <TableCell>
                    <AliasChips tag={tag} onDeleteAlias={deleteAlias} />
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap justify-end gap-1">
                      <Button size="sm" variant="outline" onClick={() => setAction({ kind: "rename", tag })}>
                        Rename
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setAction({ kind: "merge", tag })}>
                        Merge
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setAction({ kind: "split", tag })}>
                        Split
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setAction({ kind: "alias", tag })}>
                        Alias
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {!loadingTags && visibleTags.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="py-10 text-center text-sm text-muted-foreground">
                    No tags match the current filters.
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <TagActionModal
        action={action}
        tags={tags}
        saving={savingAction}
        onClose={() => setAction(null)}
        onSubmit={submitAction}
      />
      <TagEntriesModal
        tag={entriesTag}
        entries={entriesTag ? entriesForTag(entriesTag) : []}
        onClose={() => setEntriesTag(null)}
        onOpenEntry={(paper) => {
          setEntriesTag(null);
          onOpenEntry(paper);
        }}
      />
    </section>
  );
}

function TagEntriesModal({
  tag,
  entries,
  onClose,
  onOpenEntry
}: {
  tag: AdminTag | null;
  entries: Paper[];
  onClose: () => void;
  onOpenEntry: (paper: Paper) => void;
}) {
  if (!tag) return null;

  return (
    <div
      aria-modal="true"
      className="fixed inset-0 z-50 grid place-items-center bg-foreground/30 p-4 backdrop-blur-sm"
      onMouseDown={onClose}
      role="dialog"
    >
      <div
        className="flex max-h-[82vh] w-full max-w-3xl flex-col border bg-card shadow-xl"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 border-b bg-muted/40 px-4 py-3">
          <div>
            <div className="text-base font-semibold">{tag.label}</div>
            <div className="mt-1 text-sm text-muted-foreground">
              {tagTypeLabels[tag.type]} · {entries.length} {entries.length === 1 ? "entry" : "entries"}
            </div>
          </div>
          <Button aria-label="Close" onClick={onClose} size="icon" type="button" variant="ghost">
            <X className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
        <div className="overflow-y-auto">
          {entries.length > 0 ? (
            <div className="divide-y">
              {entries.map((paper) => (
                <button
                  className="grid w-full gap-1 px-4 py-3 text-left transition-colors hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  key={paper.id}
                  onClick={() => onOpenEntry(paper)}
                  type="button"
                >
                  <span className="text-sm font-semibold leading-5">{paper.title}</span>
                  <span className="text-xs text-muted-foreground">
                    {[formatShortAuthors(paper.authors), paper.year, paper.publication].filter(Boolean).join(" · ")}
                  </span>
                  {paper.keyFinding ? <span className="line-clamp-2 text-sm text-muted-foreground">{paper.keyFinding}</span> : null}
                </button>
              ))}
            </div>
          ) : (
            <div className="px-4 py-10 text-center text-sm text-muted-foreground">
              No loaded entries currently use this tag.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AliasChips({
  tag,
  onDeleteAlias
}: {
  tag: AdminTag;
  onDeleteAlias: (tag: AdminTag, alias: TagAlias) => Promise<void>;
}) {
  if (tag.aliases.length === 0) return <span className="text-sm text-muted-foreground">None</span>;

  return (
    <div className="flex flex-wrap gap-1.5">
      {tag.aliases.map((alias) => (
        <Badge className="gap-1 rounded-sm bg-background text-xs" variant="outline" key={alias.id}>
          {alias.label}
          <button
            aria-label={`Delete alias ${alias.label}`}
            className="-mr-1 inline-flex h-4 w-4 items-center justify-center rounded-sm hover:bg-muted"
            onClick={() => void onDeleteAlias(tag, alias)}
            type="button"
          >
            <X className="h-3 w-3" aria-hidden="true" />
          </button>
        </Badge>
      ))}
    </div>
  );
}

function TagActionModal({
  action,
  tags,
  saving,
  onClose,
  onSubmit
}: {
  action: TagAction | null;
  tags: AdminTag[];
  saving: boolean;
  onClose: () => void;
  onSubmit: (payload: Record<string, unknown>) => Promise<void>;
}) {
  const [label, setLabel] = useState("");
  const [targetTagId, setTargetTagId] = useState("");
  const [labels, setLabels] = useState("");

  useEffect(() => {
    if (!action) return;
    setLabel(action.kind === "rename" ? action.tag.label : "");
    setTargetTagId("");
    setLabels("");
  }, [action]);

  if (!action) return null;

  const sameTypeTags = tags.filter((tag) => tag.type === action.tag.type && tag.id !== action.tag.id);
  const title =
    action.kind === "rename"
      ? "Rename tag"
      : action.kind === "merge"
        ? "Merge tag"
        : action.kind === "split"
          ? "Split tag"
          : "Add alias";

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (action?.kind === "rename" || action?.kind === "alias") {
      void onSubmit({ label });
    } else if (action?.kind === "merge") {
      void onSubmit({ targetTagId });
    } else if (action?.kind === "split") {
      void onSubmit({ labels: splitList(labels) });
    }
  }

  return (
    <div
      aria-modal="true"
      className="fixed inset-0 z-50 grid place-items-center bg-foreground/30 p-4 backdrop-blur-sm"
      onMouseDown={onClose}
      role="dialog"
    >
      <form
        className="w-full max-w-xl border bg-card shadow-xl"
        onMouseDown={(event) => event.stopPropagation()}
        onSubmit={submit}
      >
        <div className="flex items-start justify-between gap-3 border-b bg-muted/40 px-4 py-3">
          <div>
            <div className="text-base font-semibold">{title}</div>
            <div className="mt-1 text-sm text-muted-foreground">
              {tagTypeLabels[action.tag.type]} · {action.tag.label}
            </div>
          </div>
          <Button aria-label="Close" onClick={onClose} size="icon" type="button" variant="ghost">
            <X className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
        <div className="grid gap-4 px-4 py-4">
          {action.kind === "rename" ? (
            <Field label="New label">
              <Input value={label} onChange={(event) => setLabel(event.target.value)} autoFocus />
            </Field>
          ) : null}

          {action.kind === "alias" ? (
            <Field label="Alias label">
              <Input value={label} onChange={(event) => setLabel(event.target.value)} autoFocus placeholder="Alternate AI/user label" />
            </Field>
          ) : null}

          {action.kind === "merge" ? (
            <Field label="Merge into">
              <Select value={targetTagId} onChange={(event) => setTargetTagId(event.target.value)} autoFocus>
                <option value="">Choose a {tagTypeLabels[action.tag.type].toLowerCase()} tag</option>
                {sameTypeTags.map((tag) => (
                  <option value={tag.id} key={tag.id}>
                    {tag.label} ({tag.entryCount})
                  </option>
                ))}
              </Select>
            </Field>
          ) : null}

          {action.kind === "split" ? (
            <Field label="Replacement tags">
              <Textarea
                value={labels}
                onChange={(event) => setLabels(event.target.value)}
                autoFocus
                placeholder="Asia, Bangladesh, India, Indonesia"
              />
            </Field>
          ) : null}

          <div className="rounded-sm border bg-muted/20 px-3 py-2 text-sm text-muted-foreground">
            {action.kind === "rename"
              ? "Renaming changes the tag label everywhere and stores the old label as an alias."
              : action.kind === "merge"
                ? "Merging moves all entries and aliases into the target tag, then removes this tag."
                : action.kind === "split"
                  ? "Splitting replaces this tag on every entry with the replacement tags."
                  : "Aliases teach future AI intake to map alternate labels to this canonical tag."}
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t px-4 py-3">
          <Button disabled={saving} type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={saving || (action.kind === "merge" && !targetTagId)} type="submit">
            {saving ? "Saving..." : "Save"}
          </Button>
        </div>
      </form>
    </div>
  );
}

function PapersTable({
  papers,
  onOpen
}: {
  papers: Paper[];
  onOpen: (paper: Paper) => void;
}) {
  if (papers.length === 0) return <EmptyPanel />;

  return (
    <>
      <MobilePapersList papers={papers} onOpen={onOpen} />
      <div className="index-surface hidden overflow-hidden border bg-card md:block">
        <Table className="min-w-[860px]">
          <TableHeader className="border-b bg-secondary/80 text-[11px] uppercase tracking-wide">
            <TableRow>
              <TableHead className="w-[38%]">Entry</TableHead>
              <TableHead className="w-[7%] text-center">Year</TableHead>
              <TableHead className="w-[13%]">Geography</TableHead>
              <TableHead className="w-[14%]">Type</TableHead>
              <TableHead className="w-[28%]">Key finding</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {papers.map((paper) => {
              const sourceUrl = normalizeUrl(paper.url || (paper.doi ? `https://doi.org/${paper.doi}` : ""));

              return (
                <TableRow
                  className="group cursor-pointer align-top hover:bg-accent/30"
                  key={paper.id}
                  onClick={() => onOpen(paper)}
                >
                  <TableCell className="py-3">
                    <div className="flex max-w-[620px] items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="research-title line-clamp-2 text-base font-semibold leading-5 text-foreground group-hover:underline">
                          {paper.title}
                        </div>
                        <div className="mt-1.5 truncate text-xs leading-5 text-muted-foreground">
                          {[formatShortAuthors(paper.authors), paper.publication].filter(Boolean).join(" · ")}
                        </div>
                        {paper.doi ? (
                          <div className="mt-1.5 truncate font-mono text-[11px] leading-4 text-muted-foreground">
                            <span className="mr-1 font-sans font-semibold uppercase">DOI</span>
                            {paper.doi}
                          </div>
                        ) : null}
                      </div>
                      {sourceUrl ? (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 shrink-0 opacity-70 hover:opacity-100"
                          title="Open source"
                          aria-label="Open source"
                          onClick={(event) => {
                            event.stopPropagation();
                            window.open(sourceUrl, "_blank", "noopener,noreferrer");
                          }}
                        >
                          <ExternalLink className="h-4 w-4" aria-hidden="true" />
                        </Button>
                      ) : null}
                    </div>
                  </TableCell>
                  <TableCell className="py-3 text-center font-mono text-[13px] text-muted-foreground">
                    {paper.year || "—"}
                  </TableCell>
                  <TableCell className="py-3">
                    <GeographyBadges paper={paper} />
                  </TableCell>
                  <TableCell className="py-3">
                    <div className="text-sm font-medium leading-5 text-foreground">
                      {toTitleCase(paper.sourceType || "Unknown")}
                    </div>
                  </TableCell>
                  <TableCell className="py-3">
                    <div className="line-clamp-3 text-sm leading-5 text-foreground/90">
                      {paper.keyFinding || <span className="text-muted-foreground">Unknown</span>}
                    </div>
                    <FacetTrail paper={paper} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </>
  );
}

function MobilePapersList({ papers, onOpen }: { papers: Paper[]; onOpen: (paper: Paper) => void }) {
  return (
    <div className="grid gap-2 md:hidden">
      {papers.map((paper) => {
        const sourceUrl = normalizeUrl(paper.url || (paper.doi ? `https://doi.org/${paper.doi}` : ""));

        return (
          <Card
            className="cursor-pointer rounded-none border-l-2 border-l-accent shadow-none transition-colors hover:bg-accent/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            key={paper.id}
            onClick={() => onOpen(paper)}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onOpen(paper);
              }
            }}
            role="button"
            tabIndex={0}
          >
            <CardContent className="grid gap-1.5 p-3">
              <div className="flex items-start gap-2">
                <div className="min-w-0 flex-1">
                  <div className="research-title line-clamp-2 text-[15px] font-semibold leading-5 text-foreground">
                    {paper.title}
                  </div>
                  <div className="mt-1 line-clamp-1 text-xs leading-4 text-muted-foreground">
                    {[formatShortAuthors(paper.authors), paper.publication].filter(Boolean).join(" · ")}
                  </div>
                </div>
                {sourceUrl ? (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="-mr-1 -mt-1 h-9 w-9 shrink-0 opacity-75"
                    title="Open source"
                    aria-label="Open source"
                    onClick={(event) => {
                      event.stopPropagation();
                      window.open(sourceUrl, "_blank", "noopener,noreferrer");
                    }}
                  >
                    <ExternalLink className="h-4 w-4" aria-hidden="true" />
                  </Button>
                ) : null}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {paper.year ? (
                  <Badge className="rounded-sm px-1.5 py-0 text-[11px] leading-5" variant="outline">
                    {paper.year}
                  </Badge>
                ) : null}
                {geographyTagsForPaper(paper).slice(0, 2).map((tag) => (
                  <Badge className="rounded-sm px-1.5 py-0 text-[11px] leading-5" variant="outline" key={tag}>
                    {tag}
                  </Badge>
                ))}
                <Badge className="rounded-sm px-1.5 py-0 text-[11px] leading-5" variant="muted">
                  {toTitleCase(paper.sourceType || "Unknown")}
                </Badge>
              </div>
              {paper.doi ? (
                <div className="truncate font-mono text-[11px] leading-4 text-muted-foreground">
                  <span className="mr-1 font-sans font-semibold uppercase">DOI</span>
                  {paper.doi}
                </div>
              ) : null}
              {paper.keyFinding ? (
                <div className="line-clamp-1 text-[13px] leading-5 text-foreground/85">{paper.keyFinding}</div>
              ) : null}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

function IntakeView() {
  const [file, setFile] = useState<File | null>(null);
  const [intakeForm, setIntakeForm] = useState({
    sourceUrl: "",
    notes: ""
  });
  const [submittingIntake, setSubmittingIntake] = useState(false);
  const [intakeError, setIntakeError] = useState("");
  const [submittedMessage, setSubmittedMessage] = useState("");
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  function updateIntakeForm(key: keyof typeof intakeForm, value: string) {
    setIntakeForm({ ...intakeForm, [key]: value });
  }

  function setSubmissionFile(nextFile: File | null) {
    setSubmittedMessage("");
    if (nextFile && !isPdfFile(nextFile)) {
      setIntakeError("Upload a PDF file.");
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    setIntakeError("");
    setFile(nextFile);
  }

  function clearSubmissionFile() {
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  async function submitSource(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmittingIntake(true);
    setIntakeError("");
    setSubmittedMessage("");

    try {
      const hasFile = Boolean(file);
      const hasUrl = Boolean(intakeForm.sourceUrl.trim());
      if (!hasFile && !hasUrl) throw new Error("Add a PDF, a source URL, or both.");

      const formData = new FormData();
      formData.set("sourceKind", hasFile ? "pdf" : "url");
      formData.set("sourceUrl", intakeForm.sourceUrl);
      formData.set("notes", intakeForm.notes);
      if (file) formData.set("file", file);

      const sourceResponse = await fetch("/api/intake/sources", {
        method: "POST",
        body: formData
      });
      if (!sourceResponse.ok) throw new Error(await sourceResponse.text());
      await sourceResponse.json();

      setIntakeForm({
        sourceUrl: "",
        notes: ""
      });
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      setSubmittedMessage("Source submitted. The research team will review it before adding it to the database.");
    } catch (err) {
      setIntakeError(err instanceof Error ? err.message : "Unable to submit source");
    } finally {
      setSubmittingIntake(false);
    }
  }

  return (
    <section className="mx-auto grid max-w-2xl gap-4">
      <div>
        <p className="text-sm font-medium text-muted-foreground">Contribute research</p>
        <h1 className="mt-1 text-3xl font-semibold tracking-normal md:text-4xl">Submit a source</h1>
        <p className="mt-3 text-base leading-7 text-muted-foreground">
          Share a paper, report, dataset, or policy document related to lead-acid battery recycling.
        </p>
      </div>

      <Card className="rounded-none">
        <form onSubmit={submitSource}>
          <CardHeader className="border-b px-4 py-4">
            <CardTitle className="text-base">Source details</CardTitle>
            <CardDescription>Upload a PDF, paste a URL, or provide both.</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 px-4 py-4">
            {intakeError ? (
              <Alert variant="destructive">
                <AlertDescription>{intakeError}</AlertDescription>
              </Alert>
            ) : null}
            {submittedMessage ? (
              <Alert>
                <AlertDescription>{submittedMessage}</AlertDescription>
              </Alert>
            ) : null}
            <Field label="PDF">
              <div
                className={cn(
                  "grid min-h-[156px] place-items-center rounded-md border border-dashed bg-muted/20 px-4 py-6 text-center transition-colors",
                  isDraggingFile && "border-foreground bg-muted/60"
                )}
                onDragEnter={(event) => {
                  event.preventDefault();
                  setIsDraggingFile(true);
                }}
                onDragOver={(event) => {
                  event.preventDefault();
                  setIsDraggingFile(true);
                }}
                onDragLeave={(event) => {
                  event.preventDefault();
                  setIsDraggingFile(false);
                }}
                onDrop={(event) => {
                  event.preventDefault();
                  setIsDraggingFile(false);
                  setSubmissionFile(event.dataTransfer.files?.[0] || null);
                }}
              >
                <input
                  accept=".pdf,application/pdf"
                  className="sr-only"
                  onChange={(event) => setSubmissionFile(event.target.files?.[0] || null)}
                  ref={fileInputRef}
                  type="file"
                />
                <div className="grid gap-3">
                  <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-md border bg-background">
                    <FileUp className="h-5 w-5" aria-hidden="true" />
                  </div>
                  {file ? (
                    <div className="grid gap-1">
                      <div className="text-sm font-medium">{file.name}</div>
                      <div className="text-xs text-muted-foreground">{formatBytes(file.size)}</div>
                    </div>
                  ) : (
                    <div className="grid gap-1">
                      <div className="text-sm font-medium">Drop a PDF here</div>
                      <div className="text-xs text-muted-foreground">or choose one from your computer</div>
                    </div>
                  )}
                  <div className="flex justify-center gap-2">
                    <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()}>
                      Choose file
                    </Button>
                    {file ? (
                      <Button type="button" variant="ghost" onClick={clearSubmissionFile}>
                        Clear
                      </Button>
                    ) : null}
                  </div>
                </div>
              </div>
            </Field>
            <Field label="Source URL">
              <Input
                value={intakeForm.sourceUrl}
                onChange={(event) => updateIntakeForm("sourceUrl", event.target.value)}
                placeholder="https://doi.org/... or https://..."
              />
            </Field>
            <Field label="Notes">
              <Textarea
                className="min-h-[140px]"
                value={intakeForm.notes}
                onChange={(event) => updateIntakeForm("notes", event.target.value)}
                placeholder="Why is this relevant? Add any context, geography, or policy angle we should pay attention to."
              />
            </Field>
            <Button disabled={submittingIntake} type="submit">
              <FileUp className="h-4 w-4" aria-hidden="true" />
              {submittingIntake ? "Submitting..." : "Submit source"}
            </Button>
          </CardContent>
        </form>
      </Card>
    </section>
  );

}

function PaperPage({
  paper,
  loading,
  onBack,
  onEdit
}: {
  paper?: Paper;
  loading: boolean;
  onBack: () => void;
  onEdit: (paper: Paper) => void;
}) {
  if (!paper) {
    return (
      <Card className="rounded-none">
        <CardHeader className="items-start space-y-0 border-b px-5 py-4">
          <Button variant="ghost" size="sm" onClick={onBack}>
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Back to database
          </Button>
        </CardHeader>
        <CardContent className="px-5 py-10 text-center">
          <CardTitle>{loading ? "Loading entry" : "Entry not found"}</CardTitle>
          <CardDescription className="mt-2">
            {loading ? "The entry is being loaded." : "This entry may have been removed or the URL is incorrect."}
          </CardDescription>
        </CardContent>
      </Card>
    );
  }

  const url = normalizeUrl(paper.url || (paper.doi ? `https://doi.org/${paper.doi}` : ""));
  const canManageEntries = Boolean(window.localStorage.getItem("lbr_admin_token"));
  const actionCount = Number(Boolean(url)) + Number(canManageEntries);

  return (
    <article className="grid gap-5">
      <div className="border-b pb-3">
        <Button variant="ghost" size="sm" className="-ml-2" onClick={onBack}>
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Back to database
        </Button>
      </div>
      <div className="grid gap-8 xl:grid-cols-[minmax(0,1fr)_320px]">
        <div className="min-w-0">
          <h1 className="research-title max-w-4xl text-[31px] font-semibold leading-[1.13] md:text-[42px]">
            {paper.title}
          </h1>
          <CitationSummary paper={paper} />
          <EntryFacts paper={paper} />
          <AbstractDisclosure body={paper.abstract} />

          <div className="mt-8 grid gap-6">
            <TextBlock title="Core question" body={paper.coreQuestion} />
            <TextBlock title="Key finding" body={paper.keyFinding} />
            <DetailedSummaryBlock body={paper.summary} />
          </div>
        </div>

        <aside className="index-surface space-y-6 border bg-card p-4 xl:sticky xl:top-20 xl:self-start">
          {actionCount > 0 ? (
          <div className={cn("grid gap-2", actionCount > 1 ? "grid-cols-2" : "grid-cols-1")}>
            {url ? (
              <Button variant="outline" className="justify-center" onClick={() => window.open(url, "_blank", "noopener,noreferrer")}>
                <ExternalLink className="h-4 w-4" aria-hidden="true" />
                Source
              </Button>
            ) : null}
            {canManageEntries ? (
            <Button className="justify-center" onClick={() => onEdit(paper)}>
              <Pencil className="h-4 w-4" aria-hidden="true" />
              Edit
            </Button>
            ) : null}
          </div>
          ) : null}
          <SidebarSection title="Copy citation">
            <CitationCopy paper={paper} />
          </SidebarSection>
          <SidebarSection title="Research facets">
            <div className="grid gap-4">
              <FacetGroup title="Population" tags={paper.populationTags} />
              <FacetGroup title="Exposure" tags={paper.exposureTags} />
              <FacetGroup title="Topics" tags={paper.topics} />
              <FacetGroup title="Policy levers" tags={paper.policyTags} />
            </div>
          </SidebarSection>
          <SidebarSection title="Entry metadata">
            <MetadataList
              items={[
                { label: "DOI", value: paper.doi, mono: true },
                { label: "Source type", value: toTitleCase(paper.sourceType || "Unknown") },
                { label: "Updated", value: formatTimestamp(paper.updatedAt) || paper.updatedAt }
              ]}
            />
          </SidebarSection>
        </aside>
      </div>
    </article>
  );
}

function AdminReviewQueue({
  onManageEntries,
  onPublished
}: {
  onManageEntries: () => void;
  onPublished: (paperId: string) => Promise<void>;
}) {
  const [adminToken, setAdminToken] = useState(() => window.localStorage.getItem("lbr_admin_token") || "");
  const [tokenInput, setTokenInput] = useState(adminToken);
  const [requiresToken, setRequiresToken] = useState(true);
  const [localBypass, setLocalBypass] = useState(false);
  const [sources, setSources] = useState<SourceArtifact[]>([]);
  const [analyses, setAnalyses] = useState<AiAnalysis[]>([]);
  const [selectedSourceId, setSelectedSourceId] = useState("");
  const [draftEntry, setDraftEntry] = useState<EditablePaper>(emptyPaper);
  const [loadingQueue, setLoadingQueue] = useState(true);
  const [adminError, setAdminError] = useState("");
  const [generating, setGenerating] = useState(false);
  const [bulkGenerating, setBulkGenerating] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [deletingSource, setDeletingSource] = useState(false);

  const selectedSource = sources.find((source) => source.id === selectedSourceId) || null;
  const selectedAnalysis = selectedSource ? latestAnalysisForSource(analyses, selectedSource.id) : null;
  const selectedAnalysisReady = selectedAnalysis ? isAnalysisReady(selectedAnalysis) : false;
  const selectedAnalysisBusy = selectedAnalysis ? isAnalysisInProgress(selectedAnalysis) : false;
  const queueableCount = sources.filter((source) => canQueueAnalysis(latestAnalysisForSource(analyses, source.id))).length;
  const runningCount = analyses.filter(isAnalysisInProgress).length;

  async function adminRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
    const headers = new Headers(init.headers);
    if (adminToken) headers.set("authorization", `Bearer ${adminToken}`);
    if (init.body && !headers.has("content-type")) headers.set("content-type", "application/json");

    const response = await fetch(path, { ...init, headers });
    if (!response.ok) {
      if (response.status === 401) {
        window.localStorage.removeItem("lbr_admin_token");
      }
      throw new Error(await response.text());
    }

    return response.json() as Promise<T>;
  }

  async function loadQueue(preferredSourceId = selectedSourceId) {
    setLoadingQueue(true);
    setAdminError("");

    try {
      const status = await apiGet<{ requiresToken: boolean; localBypass: boolean }>("/api/admin/status");
      setRequiresToken(status.requiresToken);
      setLocalBypass(status.localBypass);

      if (status.requiresToken && !adminToken) {
        setSources([]);
        setAnalyses([]);
        return;
      }

      const queue = await adminRequest<{ sources: SourceArtifact[]; analyses: AiAnalysis[] }>("/api/admin/intake/sources");
      setSources(queue.sources);
      setAnalyses(queue.analyses);

      const nextSourceId = queue.sources.some((source) => source.id === preferredSourceId)
        ? preferredSourceId
        : queue.sources[0]?.id || "";
      setSelectedSourceId(nextSourceId);
      const nextAnalysis = nextSourceId ? latestAnalysisForSource(queue.analyses, nextSourceId) : null;
      setDraftEntry(toEditableDraft(nextAnalysis?.draftEntry));
    } catch (err) {
      setAdminError(err instanceof Error ? err.message : "Unable to load admin queue");
    } finally {
      setLoadingQueue(false);
    }
  }

  useEffect(() => {
    void loadQueue();
  }, [adminToken]);

  useEffect(() => {
    if ((requiresToken && !adminToken) || runningCount === 0) return;
    const intervalId = window.setInterval(() => {
      void loadQueue(selectedSourceId);
    }, 3000);

    return () => window.clearInterval(intervalId);
  }, [adminToken, requiresToken, runningCount, selectedSourceId]);

  function saveAdminToken(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    window.localStorage.setItem("lbr_admin_token", tokenInput);
    setAdminToken(tokenInput);
  }

  function selectSource(source: SourceArtifact) {
    setSelectedSourceId(source.id);
    const analysis = latestAnalysisForSource(analyses, source.id);
    setDraftEntry(toEditableDraft(analysis?.draftEntry));
  }

  function updateDraft<K extends keyof EditablePaper>(key: K, value: EditablePaper[K]) {
    setDraftEntry({ ...draftEntry, [key]: value });
  }

  async function generateDraft() {
    if (!selectedSource) return;
    setGenerating(true);
    setAdminError("");

    try {
      const response = await adminRequest<{ analysis: AiAnalysis }>(`/api/admin/intake/sources/${selectedSource.id}/analyze`, {
        method: "POST"
      });
      setAnalyses((current) => [response.analysis, ...current.filter((analysis) => analysis.id !== response.analysis.id)]);
      setDraftEntry(toEditableDraft(response.analysis.draftEntry));
      await loadQueue(selectedSource.id);
    } catch (err) {
      setAdminError(err instanceof Error ? err.message : "Unable to generate AI draft");
    } finally {
      setGenerating(false);
    }
  }

  async function bulkGenerateDrafts() {
    setBulkGenerating(true);
    setAdminError("");

    try {
      const response = await adminRequest<{ queued: AiAnalysis[]; queuedCount: number; skippedCount: number }>(
        "/api/admin/intake/sources/bulk-analyze",
        {
          method: "POST",
          body: JSON.stringify({ limit: 25 })
        }
      );
      setAnalyses((current) => [
        ...response.queued,
        ...current.filter((analysis) => !response.queued.some((queued) => queued.id === analysis.id))
      ]);
      await loadQueue();
    } catch (err) {
      setAdminError(err instanceof Error ? err.message : "Unable to queue AI summaries");
    } finally {
      setBulkGenerating(false);
    }
  }

  async function deleteSelectedSource() {
    if (!selectedSource) return;

    const confirmed = window.confirm(
      "Delete this submitted source from the review queue?\n\nThis removes the queued source and any AI drafts. Published database entries will not be deleted."
    );
    if (!confirmed) return;

    const deletedSourceId = selectedSource.id;
    const nextSourceId = sources.find((source) => source.id !== deletedSourceId)?.id || "";
    setDeletingSource(true);
    setAdminError("");

    try {
      await adminRequest<{ ok: boolean }>(`/api/admin/intake/sources/${deletedSourceId}`, {
        method: "DELETE"
      });
      setSources((current) => current.filter((source) => source.id !== deletedSourceId));
      setAnalyses((current) => current.filter((analysis) => analysis.sourceArtifactId !== deletedSourceId));
      setSelectedSourceId(nextSourceId);
      setDraftEntry(emptyPaper);
      await loadQueue(nextSourceId);
    } catch (err) {
      setAdminError(err instanceof Error ? err.message : "Unable to delete submitted source");
    } finally {
      setDeletingSource(false);
    }
  }

  async function publishDraft() {
    if (!selectedAnalysis) return;
    setPublishing(true);
    setAdminError("");

    try {
      const response = await adminRequest<{ paper: Paper; analysis: AiAnalysis }>(
        `/api/admin/intake/analyses/${selectedAnalysis.id}/publish`,
        {
          method: "POST",
          body: JSON.stringify({ draftEntry })
        }
      );
      await onPublished(response.paper.id);
    } catch (err) {
      setAdminError(err instanceof Error ? err.message : "Unable to publish entry");
    } finally {
      setPublishing(false);
    }
  }

  if (requiresToken && !adminToken) {
    return (
      <section className="mx-auto grid max-w-md gap-4">
        <div>
          <p className="text-sm font-medium text-muted-foreground">Admin</p>
          <h1 className="mt-1 text-3xl font-semibold tracking-normal">Review queue</h1>
        </div>
        <Card className="rounded-none">
          <form onSubmit={saveAdminToken}>
            <CardHeader className="border-b px-4 py-4">
              <CardTitle className="text-base">Admin access</CardTitle>
              <CardDescription>Enter the admin token to review submitted sources.</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 px-4 py-4">
              {adminError ? (
                <Alert variant="destructive">
                  <AlertDescription>{adminError}</AlertDescription>
                </Alert>
              ) : null}
              <Field label="Admin token">
                <Input
                  autoComplete="current-password"
                  onChange={(event) => setTokenInput(event.target.value)}
                  type="password"
                  value={tokenInput}
                />
              </Field>
              <Button type="submit">Open review queue</Button>
            </CardContent>
          </form>
        </Card>
      </section>
    );
  }

  return (
    <section className="grid gap-4 xl:grid-cols-[360px_minmax(0,1fr)]">
      <div className="space-y-4">
        <div>
          <p className="text-sm font-medium text-muted-foreground">Admin</p>
          <h1 className="mt-1 text-3xl font-semibold tracking-normal">Review queue</h1>
          {localBypass ? <p className="mt-2 text-sm text-muted-foreground">Local admin bypass is active.</p> : null}
        </div>
        <Button variant="outline" onClick={onManageEntries}>
          <Library className="h-4 w-4" aria-hidden="true" />
          Manage entries
        </Button>

        <Card className="rounded-none">
          <CardHeader className="border-b px-4 py-3">
            <div className="flex items-start justify-between gap-3">
              <div>
                <CardTitle className="text-base">Submitted sources</CardTitle>
                <CardDescription>
                  {loadingQueue
                    ? "Loading queue"
                    : `${sources.length} submissions${runningCount ? ` · ${runningCount} running` : ""}`}
                </CardDescription>
              </div>
              <div className="flex flex-wrap justify-end gap-2">
                <Button disabled={queueableCount === 0 || bulkGenerating} size="sm" onClick={bulkGenerateDrafts}>
                  <SparklesIcon />
                  {bulkGenerating ? "Queuing..." : `Queue missing (${queueableCount})`}
                </Button>
                <Button size="sm" variant="outline" onClick={() => void loadQueue()}>
                  <RefreshCw className="h-4 w-4" aria-hidden="true" />
                  Refresh
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y">
              {sources.map((source) => {
                const analysis = latestAnalysisForSource(analyses, source.id);
                const active = selectedSourceId === source.id;
                return (
                  <button
                    className={cn("grid w-full gap-1 px-4 py-3 text-left hover:bg-muted/50", active && "bg-muted/70")}
                    key={source.id}
                    onClick={() => selectSource(source)}
                    type="button"
                  >
                    <span className="line-clamp-1 text-sm font-medium">{sourceTitle(source)}</span>
                    <span className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                      <span>{submissionStatus(analysis)} · {formatTimestamp(source.createdAt)}</span>
                      {source.duplicateConfidence ? (
                        <Badge variant="outline" className="rounded-sm bg-amber-50 text-[11px] text-amber-900">
                          Possible duplicate
                        </Badge>
                      ) : null}
                    </span>
                  </button>
                );
              })}
              {!loadingQueue && sources.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-muted-foreground">No submitted sources yet.</div>
              ) : null}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="rounded-none">
        <CardHeader className="flex-row items-start justify-between gap-3 space-y-0 border-b px-4 py-4">
          <div>
            <CardTitle className="text-base">{selectedSource ? sourceTitle(selectedSource) : "Select a source"}</CardTitle>
            <CardDescription>{selectedSource ? "Review the submitted source, then queue background AI extraction." : "Choose a submission from the queue."}</CardDescription>
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            <Button disabled={!selectedSource || deletingSource} onClick={deleteSelectedSource} variant="outline">
              <Trash2 className="h-4 w-4" aria-hidden="true" />
              {deletingSource ? "Deleting..." : "Delete from queue"}
            </Button>
            <Button disabled={!selectedSource || generating || deletingSource || selectedAnalysisBusy} onClick={generateDraft}>
              <SparklesIcon />
              {generateDraftLabel({ generating, selectedAnalysis })}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="grid gap-5 px-4 py-4">
          {adminError ? (
            <Alert variant="destructive">
              <AlertDescription>{adminError}</AlertDescription>
            </Alert>
          ) : null}

          {selectedSource ? (
            <>
              <DuplicateWarning source={selectedSource} onSelectSource={setSelectedSourceId} />
              <div className="grid gap-3 border-b pb-4 md:grid-cols-3">
                <MetadataField label="Submitted" value={formatTimestamp(selectedSource.createdAt)} />
                <MetadataField label="Source type" value={toTitleCase(selectedSource.sourceKind || "source")} />
                <MetadataField label="Status" value={submissionStatus(selectedAnalysis)} />
              </div>
              <MetadataList
                items={[
                  { label: "File", value: selectedSource.filename || "No file uploaded" },
                  { label: "URL", value: selectedSource.sourceUrl || "No URL provided" },
                  { label: "Extraction", value: sourceExtractionSummary(selectedSource) },
                  {
                    label: "Readable source text",
                    value: selectedSource.extractedText
                      ? `${selectedSource.extractedText.length.toLocaleString()} characters`
                      : "No extracted text yet"
                  },
                  { label: "Notes", value: selectedSource.sourceMetadata.notes || "No notes provided" }
                ]}
              />

              {selectedAnalysis && selectedAnalysisReady ? (
                <div className="grid gap-4 border-t pt-5">
                  <div className="grid gap-2 border-b pb-4 text-sm md:grid-cols-4">
                    <MetadataField label="Draft status" value={selectedAnalysis.status} />
                    <MetadataField label="Confidence" value={`${selectedAnalysis.confidence}%`} />
                    <MetadataField label="Provider" value={selectedAnalysis.provider} />
                    <MetadataField label="Model" value={selectedAnalysis.model} />
                  </div>

                  <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_120px]">
                    <Field label="Title">
                      <Input value={draftEntry.title} onChange={(event) => updateDraft("title", event.target.value)} />
                    </Field>
                    <Field label="Year">
                      <Input
                        type="number"
                        value={draftEntry.year ?? ""}
                        onChange={(event) => updateDraft("year", event.target.value ? Number(event.target.value) : null)}
                      />
                    </Field>
                  </div>
                  <div className="grid gap-4 md:grid-cols-2">
                    <Field label="Authors">
                      <Input value={draftEntry.authors.join(", ")} onChange={(event) => updateDraft("authors", splitList(event.target.value))} />
                    </Field>
                    <Field label="Publication">
                      <Input value={draftEntry.publication} onChange={(event) => updateDraft("publication", event.target.value)} />
                    </Field>
                  </div>
                  <div className="grid gap-4 md:grid-cols-2">
                    <Field label="DOI">
                      <Input value={draftEntry.doi} onChange={(event) => updateDraft("doi", event.target.value)} />
                    </Field>
                    <Field label="Source URL">
                      <Input value={draftEntry.url} onChange={(event) => updateDraft("url", event.target.value)} />
                    </Field>
                  </div>
                  <Field label="Geography tags">
                    <Input
                      value={draftEntry.geographyTags.join(", ")}
                      onChange={(event) => updateDraft("geographyTags", splitList(event.target.value))}
                      placeholder="Global, LMICs, Vietnam, United States, Mexico"
                    />
                  </Field>
                  <Field label="Core question">
                    <Textarea value={draftEntry.coreQuestion} onChange={(event) => updateDraft("coreQuestion", event.target.value)} />
                  </Field>
                  <Field label="Key finding">
                    <Textarea value={draftEntry.keyFinding} onChange={(event) => updateDraft("keyFinding", event.target.value)} />
                  </Field>
                  <Field label="Detailed summary markdown">
                    <Textarea
                      className="min-h-[220px]"
                      value={draftEntry.summary}
                      onChange={(event) => updateDraft("summary", event.target.value)}
                    />
                    <DetailedSummaryPreview body={draftEntry.summary} />
                  </Field>
                  <Field label="Abstract">
                    <Textarea value={draftEntry.abstract} onChange={(event) => updateDraft("abstract", event.target.value)} />
                  </Field>
                  <div className="grid gap-4 md:grid-cols-2">
                    <Field label="Topics">
                      <Input value={draftEntry.topics.join(", ")} onChange={(event) => updateDraft("topics", splitList(event.target.value))} />
                    </Field>
                    <Field label="Policy levers">
                      <Input value={draftEntry.policyTags.join(", ")} onChange={(event) => updateDraft("policyTags", splitList(event.target.value))} />
                    </Field>
                    <Field label="Population">
                      <Input
                        value={draftEntry.populationTags.join(", ")}
                        onChange={(event) => updateDraft("populationTags", splitList(event.target.value))}
                      />
                    </Field>
                    <Field label="Exposure">
                      <Input
                        value={draftEntry.exposureTags.join(", ")}
                        onChange={(event) => updateDraft("exposureTags", splitList(event.target.value))}
                      />
                    </Field>
                  </div>
                  <Button disabled={publishing} onClick={publishDraft}>
                    {publishing ? "Publishing..." : "Publish to database"}
                  </Button>
                </div>
              ) : selectedAnalysis ? (
                <AnalysisStatePanel analysis={selectedAnalysis} />
              ) : (
                <div className="rounded-sm border bg-muted/20 px-4 py-8 text-center text-sm text-muted-foreground">
                  No AI draft yet. Queue a background AI summary for this source.
                </div>
              )}
            </>
          ) : (
            <div className="px-4 py-12 text-center text-sm text-muted-foreground">No source selected.</div>
          )}
        </CardContent>
      </Card>
    </section>
  );
}

function EntryEditorModal({
  children,
  onClose,
  open
}: {
  children: ReactNode;
  onClose: () => void;
  open: boolean;
}) {
  useEffect(() => {
    if (!open) return;

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }

    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [onClose, open]);

  if (!open) return null;

  return (
    <div
      aria-modal="true"
      className="fixed inset-0 z-50 grid place-items-center bg-foreground/30 p-4 backdrop-blur-sm"
      onMouseDown={onClose}
      role="dialog"
    >
      <div
        className="max-h-[92vh] w-full max-w-5xl overflow-y-auto border bg-card shadow-xl"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b bg-muted/40 px-4 py-2">
          <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Entry editor</div>
          <Button aria-label="Close editor" onClick={onClose} size="icon" type="button" variant="ghost">
            <X className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
        {children}
      </div>
    </div>
  );
}

function PaperEditor({
  paper,
  setPaper,
  saving,
  onCancel,
  onSave
}: {
  paper: EditablePaper;
  setPaper: (paper: EditablePaper) => void;
  saving: boolean;
  onCancel?: () => void;
  onSave: (paper: EditablePaper) => Promise<void>;
}) {
  function update<K extends keyof EditablePaper>(key: K, value: EditablePaper[K]) {
    setPaper({ ...paper, [key]: value });
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    void onSave(paper);
  }

  return (
    <Card className="rounded-none border-0 shadow-none">
      <form onSubmit={submit}>
        <CardHeader className="sticky top-0 z-10 flex-row items-start justify-between gap-3 space-y-0 border-b bg-card px-4 py-3">
          <div>
            <CardTitle className="text-base">{paper.id ? "Edit entry" : "Add entry"}</CardTitle>
            <CardDescription>{paper.id ? "Update this published database entry." : "Create a new published database entry."}</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {onCancel ? (
              <Button disabled={saving} onClick={onCancel} type="button" variant="outline">
                Cancel
              </Button>
            ) : null}
            <Button disabled={saving} type="submit">
              {saving ? "Saving..." : "Save"}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="grid gap-4 px-4 py-4">
        <Field label="Title">
          <Input value={paper.title} onChange={(event) => update("title", event.target.value)} required />
        </Field>
        <div className="grid gap-4 md:grid-cols-2">
          <Field label="Authors">
            <Input
              value={paper.authors.join(", ")}
              onChange={(event) => update("authors", splitList(event.target.value))}
              placeholder="Author One, Author Two"
            />
          </Field>
          <Field label="Year">
            <Input
              type="number"
              value={paper.year ?? ""}
              onChange={(event) => update("year", event.target.value ? Number(event.target.value) : null)}
            />
          </Field>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <Field label="Publication">
            <Input value={paper.publication} onChange={(event) => update("publication", event.target.value)} />
          </Field>
          <Field label="Geography tags">
            <Input
              value={paper.geographyTags.join(", ")}
              onChange={(event) => update("geographyTags", splitList(event.target.value))}
              placeholder="Global, LMICs, Vietnam, United States, Mexico"
            />
          </Field>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <Field label="Source">
            <Select value={paper.sourceType} onChange={(event) => update("sourceType", event.target.value)}>
              {sourceTypeOptions.map((sourceType) => (
                <option value={sourceType} key={sourceType}>
                  {sourceType}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Type">
            <Select value={paper.evidenceType} onChange={(event) => update("evidenceType", event.target.value)}>
              {evidenceTypeOptions.map((evidenceType) => (
                <option value={evidenceType} key={evidenceType}>
                  {evidenceType}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        <div className="grid gap-4">
          <Field label="DOI">
            <Input value={paper.doi} onChange={(event) => update("doi", event.target.value)} />
          </Field>
        </div>
        <Field label="URL">
          <Input value={paper.url} onChange={(event) => update("url", event.target.value)} />
        </Field>
        <Field label="Population">
          <Input
            value={paper.populationTags.join(", ")}
            onChange={(event) => update("populationTags", splitList(event.target.value))}
            placeholder={populationOptions.slice(0, 3).join(", ")}
          />
        </Field>
        <Field label="Exposure">
          <Input
            value={paper.exposureTags.join(", ")}
            onChange={(event) => update("exposureTags", splitList(event.target.value))}
            placeholder={exposurePathwayOptions.slice(0, 3).join(", ")}
          />
        </Field>
        <Field label="Topics">
          <Input
            value={paper.topics.join(", ")}
            onChange={(event) => update("topics", splitList(event.target.value))}
            placeholder={topicOptions.slice(0, 3).join(", ")}
          />
        </Field>
        <Field label="Policy levers">
          <Input
            value={paper.policyTags.join(", ")}
            onChange={(event) => update("policyTags", splitList(event.target.value))}
            placeholder={policyTagOptions.slice(0, 3).join(", ")}
          />
        </Field>
        <Field label="Core question">
          <Textarea value={paper.coreQuestion} onChange={(event) => update("coreQuestion", event.target.value)} />
        </Field>
        <Field label="Key finding">
          <Textarea value={paper.keyFinding} onChange={(event) => update("keyFinding", event.target.value)} />
        </Field>
        <Field label="Why it matters">
          <Textarea
            value={paper.decisionRelevance}
            onChange={(event) => update("decisionRelevance", event.target.value)}
          />
        </Field>
        <Field label="Detailed summary">
          <Textarea value={paper.summary} onChange={(event) => update("summary", event.target.value)} />
        </Field>
        <Field label="Abstract">
          <Textarea value={paper.abstract} onChange={(event) => update("abstract", event.target.value)} />
        </Field>
        <Field label="Notes">
          <Textarea value={paper.notes} onChange={(event) => update("notes", event.target.value)} />
        </Field>
        </CardContent>
      </form>
    </Card>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid gap-2">
      <Label>{label}</Label>
      {children}
    </div>
  );
}

function TextBlock({ title, body, preserveWhitespace = false }: { title: string; body: string; preserveWhitespace?: boolean }) {
  if (!body) return null;
  return (
    <section className="border-t pt-4">
      <h2 className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{title}</h2>
      <p className={cn("max-w-[88ch] text-[15px] leading-7", preserveWhitespace && "whitespace-pre-wrap")}>{body}</p>
    </section>
  );
}

function DetailedSummaryBlock({ body }: { body: string }) {
  if (!body) return null;

  return (
    <section className="border-t pt-4">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <h2 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Detailed summary</h2>
        <Badge className="rounded-sm" variant="secondary">AI-generated</Badge>
      </div>
      <DetailedSummaryContent body={body} />
    </section>
  );
}

function AbstractDisclosure({ body }: { body: string }) {
  const [open, setOpen] = useState(false);
  const abstract = body.trim();
  const preview = abstract.replace(/\s+/g, " ");
  const shouldCollapse = preview.length > 360;
  const previewText = shouldCollapse ? `${preview.slice(0, 360).trim()}...` : preview;

  if (!abstract) return null;

  const content = (
    <>
      <div className="px-4 py-4">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Abstract</div>
        <p className={cn("mt-2 max-w-[96ch] text-sm leading-6", open && "whitespace-pre-wrap")}>
          {open ? abstract : previewText}
        </p>
      </div>
      {shouldCollapse ? (
        <div className="flex items-center justify-between border-t bg-card px-4 py-2 text-sm font-medium">
          <span>{open ? "Collapse abstract" : "Read full abstract"}</span>
          <ChevronDown className={cn("h-4 w-4 transition-transform", open && "rotate-180")} aria-hidden="true" />
        </div>
      ) : null}
    </>
  );

  if (!shouldCollapse) {
    return <section className="mt-5 border bg-card">{content}</section>;
  }

  return (
    <button
      className="mt-5 block w-full border bg-card text-left transition-colors hover:bg-accent/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      onClick={() => setOpen((current) => !current)}
      type="button"
    >
      {content}
    </button>
  );
}

function DetailedSummaryPreview({ body }: { body: string }) {
  if (!body) return null;

  return (
    <div className="rounded-sm border bg-muted/20 p-3">
      <div className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        Formatted preview
      </div>
      <DetailedSummaryContent body={body} compact />
    </div>
  );
}

function AnalysisStatePanel({ analysis }: { analysis: AiAnalysis }) {
  const running = isAnalysisInProgress(analysis);

  return (
    <div className="grid gap-3 rounded-sm border bg-muted/20 px-4 py-8 text-center text-sm text-muted-foreground">
      <div className="mx-auto flex items-center gap-2 text-foreground">
        {running ? <RefreshCw className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
        <span className="font-medium">{analysisStatusLabel(analysis)}</span>
      </div>
      {analysis.analysisNotes ? <p>{analysis.analysisNotes}</p> : null}
      {running ? <p>This job is running in the background. The queue refreshes automatically.</p> : null}
    </div>
  );
}

function DuplicateWarning({
  onSelectSource,
  source
}: {
  onSelectSource: (sourceId: string) => void;
  source: SourceArtifact;
}) {
  if (!source.duplicateConfidence) return null;

  const duplicateType = source.duplicateOfType === "paper" ? "published entry" : "submitted source";

  return (
    <Alert className="border-amber-300 bg-amber-50 text-amber-950">
      <AlertTitle>Possible duplicate</AlertTitle>
      <AlertDescription className="grid gap-3">
        <p>
          {source.duplicateReason || `This looks similar to an existing ${duplicateType}.`} Confidence:{" "}
          {source.duplicateConfidence}%.
        </p>
        <div className="flex flex-wrap gap-2">
          {source.duplicateOfType === "paper" && source.duplicateOfId ? (
            <Button
              size="sm"
              type="button"
              variant="outline"
              onClick={() => {
                window.history.pushState({}, "", `/papers/${source.duplicateOfId}`);
                window.dispatchEvent(new PopStateEvent("popstate"));
              }}
            >
              Open published entry
            </Button>
          ) : null}
          {source.duplicateOfType === "source" && source.duplicateOfId ? (
            <Button size="sm" type="button" variant="outline" onClick={() => onSelectSource(source.duplicateOfId)}>
              Open submitted source
            </Button>
          ) : null}
        </div>
      </AlertDescription>
    </Alert>
  );
}

function DetailedSummaryContent({ body, compact = false }: { body: string; compact?: boolean }) {
  return (
    <div className={cn("markdown-block max-w-[96ch]", compact && "markdown-block-compact")}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          a: ({ children, href }) => (
            <a href={href} target="_blank" rel="noreferrer">
              {children}
            </a>
          )
        }}
      >
        {normalizeSummaryMarkdown(body)}
      </ReactMarkdown>
    </div>
  );
}

function CitationSummary({ paper }: { paper: Paper }) {
  return (
    <section className="mt-5 grid gap-5 border-y bg-card px-4 py-4 lg:grid-cols-[minmax(0,1fr)_280px]">
      <MetadataField label="Authors" value={formatFullAuthors(paper.authors)} />
      <MetadataField label="Published in" value={[paper.publication || "Unknown publication", paper.year].filter(Boolean).join(", ")} />
    </section>
  );
}

function EntryFacts({ paper }: { paper: Paper }) {
  const facts = [
    { label: "Geography", value: geographyTagsForPaper(paper).join(", ") || "Unknown" },
    { label: "Year", value: paper.year ? String(paper.year) : "Unknown" },
    { label: "Type", value: toTitleCase(paper.evidenceType || "Unknown") },
    { label: "Source", value: toTitleCase(paper.sourceType || "Unknown") }
  ];

  return (
    <dl className="mt-4 flex flex-wrap gap-x-8 gap-y-3 border-y bg-background py-3">
      {facts.map((fact) => (
        <div className="min-w-[120px]" key={fact.label}>
          <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{fact.label}</dt>
          <dd className="mt-1 text-sm font-medium leading-5 text-foreground">{fact.value}</dd>
        </div>
      ))}
    </dl>
  );
}

function MetadataField({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid gap-1">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-base leading-6 text-foreground">{value}</div>
    </div>
  );
}

function CitationCopy({ paper }: { paper: Paper }) {
  const [format, setFormat] = useState<CitationFormat>("chicago");
  const [citation, setCitation] = useState("");
  const [loadingCitation, setLoadingCitation] = useState(false);
  const [copyState, setCopyState] = useState<"idle" | "copied" | "error">("idle");

  useEffect(() => {
    let active = true;
    setLoadingCitation(true);
    setCopyState("idle");

    apiGet<{ citation: string }>(`/api/papers/${paper.id}/citation?format=${format}`)
      .then((response) => {
        if (active) setCitation(response.citation);
      })
      .catch(() => {
        if (active) setCitation("");
      })
      .finally(() => {
        if (active) setLoadingCitation(false);
      });

    return () => {
      active = false;
    };
  }, [format, paper.id]);

  async function copyCitation() {
    if (!citation) return;

    try {
      await navigator.clipboard.writeText(citation);
      setCopyState("copied");
    } catch {
      setCopyState("error");
    }
  }

  return (
    <div className="grid gap-3">
      <Select value={format} onChange={(event) => setFormat(event.target.value as CitationFormat)}>
        {citationFormatOptions.map((option) => (
          <option value={option.value} key={option.value}>
            {option.label}
          </option>
        ))}
      </Select>
      <div className="max-h-28 overflow-auto rounded-sm border bg-muted/30 p-3 font-serif text-xs leading-5 text-muted-foreground">
        {loadingCitation ? "Loading citation..." : citation || "Citation unavailable"}
      </div>
      <Button variant="outline" className="justify-center" disabled={!citation || loadingCitation} onClick={copyCitation}>
        {copyState === "copied" ? <Check className="h-4 w-4" aria-hidden="true" /> : <Copy className="h-4 w-4" aria-hidden="true" />}
        {copyState === "copied" ? "Copied" : copyState === "error" ? "Copy failed" : "Copy citation"}
      </Button>
    </div>
  );
}

function formatFullAuthors(authors: string[]) {
  return authors.length ? authors.join(", ") : "Unknown authors";
}

function SidebarSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="border-t pt-5">
      <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</h3>
      {children}
    </section>
  );
}

function FacetGroup({ title, tags }: { title: string; tags: string[] }) {
  if (tags.length === 0) return null;
  return (
    <div className="grid gap-2">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{title}</div>
      <div className="flex flex-wrap gap-2">
        {tags.map((tag) => (
          <Badge variant="outline" className="rounded-sm bg-background text-xs" key={tag}>
            {tag}
          </Badge>
        ))}
      </div>
    </div>
  );
}

function MetadataList({
  items
}: {
  items: Array<{
    label: string;
    value?: string | number | null;
    mono?: boolean;
  }>;
}) {
  const visibleItems = items.filter((item) => item.value !== undefined && item.value !== null && item.value !== "");

  if (visibleItems.length === 0) return null;

  return (
    <dl className="divide-y divide-border text-sm">
      {visibleItems.map((item) => (
        <div className="grid gap-1 py-3 first:pt-0 last:pb-0" key={item.label}>
          <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{item.label}</dt>
          <dd className={cn("break-words leading-5 text-foreground", item.mono && "font-mono text-xs")}>
            {item.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function EmptyPanel() {
  return (
    <Card className="rounded-none">
      <CardContent className="px-4 py-10 text-center text-sm text-muted-foreground">
        No entries match the current filters.
      </CardContent>
    </Card>
  );
}

function IconButton({
  label,
  destructive,
  children,
  onClick
}: {
  label: string;
  destructive?: boolean;
  children: ReactNode;
  onClick: () => void;
}) {
  return (
    <Button
      variant={destructive ? "destructive" : "outline"}
      size="icon"
      title={label}
      aria-label={label}
      onClick={onClick}
      type="button"
    >
      {children}
    </Button>
  );
}

function FacetTrail({ paper }: { paper: Paper }) {
  const facets = [
    formatFacetSummary("Pop", paper.populationTags),
    formatFacetSummary("Exposure", paper.exposureTags),
    formatFacetSummary("Policy", paper.policyTags)
  ].filter(Boolean);

  if (facets.length === 0) return null;

  return (
    <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] leading-4 text-muted-foreground">
      {facets.map((facet) => (
        <span key={facet}>{facet}</span>
      ))}
    </div>
  );
}

function GeographyBadges({ paper, compact = false }: { paper: Paper; compact?: boolean }) {
  const tags = geographyTagsForPaper(paper);

  return (
    <div className={cn("flex flex-wrap gap-1.5", compact && "max-w-[180px]")}>
      {tags.length ? (
        tags.slice(0, compact ? 2 : 4).map((tag) => (
          <Badge className="rounded-full bg-secondary px-2 py-0 text-xs font-medium text-secondary-foreground" key={tag} variant="muted">
            {tag}
          </Badge>
        ))
      ) : (
        <span className="text-sm text-muted-foreground">Unknown</span>
      )}
    </div>
  );
}

function formatFacetSummary(label: string, values: string[]) {
  if (values.length === 0) return "";
  const visible = values.slice(0, 2).join(", ");
  const remaining = values.length > 2 ? ` +${values.length - 2}` : "";
  return `${label}: ${visible}${remaining}`;
}

function geographyTagsForPaper(
  paper: Pick<Paper, "geographyTags" | "countries" | "region" | "geography" | "geographicScope">
) {
  if ("geographyTags" in paper && Array.isArray(paper.geographyTags) && paper.geographyTags.length) {
    return paper.geographyTags;
  }
  if (paper.geographicScope === "global") return ["Global"];
  if (paper.geographicScope === "lmics") return ["LMICs"];
  const tags = [...paper.countries];
  if (tags.length === 0 && paper.region) tags.push(paper.region);
  if (tags.length === 0 && paper.geography) tags.push(paper.geography);
  return Array.from(new Set(tags));
}

function splitList(value: string) {
  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function facetOptions(values: string[]) {
  return Array.from(new Set(values.map((value) => value.trim()).filter(Boolean))).sort((a, b) => a.localeCompare(b));
}

function isPdfFile(file: File) {
  return file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
}

function formatBytes(value: number) {
  if (!Number.isFinite(value) || value <= 0) return "0 bytes";
  const units = ["bytes", "KB", "MB", "GB"];
  const index = Math.min(Math.floor(Math.log(value) / Math.log(1024)), units.length - 1);
  const amount = value / 1024 ** index;
  return `${amount >= 10 || index === 0 ? Math.round(amount) : amount.toFixed(1)} ${units[index]}`;
}

function latestAnalysisForSource(analyses: AiAnalysis[], sourceId: string) {
  return analyses.find((analysis) => analysis.sourceArtifactId === sourceId) || null;
}

function sourceTitle(source: SourceArtifact) {
  return (
    source.sourceMetadata.title ||
    source.filename ||
    source.sourceUrl ||
    source.doi ||
    "Untitled submitted source"
  );
}

function sourceExtractionSummary(source: SourceArtifact) {
  const status = toTitleCase(source.extractionStatus || "pending");
  const size = source.sizeBytes ? ` · ${formatBytes(source.sizeBytes)}` : "";
  const error = source.extractionError ? ` · ${source.extractionError}` : "";
  return `${status}${size}${error}`;
}

function submissionStatus(analysis: AiAnalysis | null) {
  if (!analysis) return "Submitted";
  if (analysis.publishedEntryId) return "Published";
  if (analysis.status === "queued") return "Queued";
  if (analysis.status === "running") return "Running";
  if (analysis.status === "failed") return "AI failed";
  if (analysis.status === "draft" && analysis.provider !== "openai") return "Local fallback draft";
  return "AI draft ready";
}

function isAnalysisInProgress(analysis: AiAnalysis) {
  return analysis.status === "queued" || analysis.status === "running";
}

function isAnalysisReady(analysis: AiAnalysis) {
  return analysis.status === "draft" || analysis.status === "published";
}

function canQueueAnalysis(analysis: AiAnalysis | null) {
  if (!analysis) return true;
  if (analysis.status === "failed") return true;
  return analysis.status === "draft" && analysis.provider !== "openai";
}

function analysisStatusLabel(analysis: AiAnalysis) {
  if (analysis.status === "queued") return "AI summary queued";
  if (analysis.status === "running") return "AI summary running";
  if (analysis.status === "failed") return "AI summary failed";
  if (analysis.status === "published") return "Published";
  if (analysis.provider !== "openai") return "Local fallback draft";
  return "AI draft ready";
}

function generateDraftLabel({
  generating,
  selectedAnalysis
}: {
  generating: boolean;
  selectedAnalysis: AiAnalysis | null;
}) {
  if (generating) return "Queuing...";
  if (!selectedAnalysis) return "Queue AI summary";
  if (selectedAnalysis.status === "queued") return "AI summary queued";
  if (selectedAnalysis.status === "running") return "AI summary running";
  if (selectedAnalysis.status === "failed") return "Retry AI draft";
  if (selectedAnalysis.provider !== "openai") return "Queue OpenAI summary";
  return "Regenerate AI draft";
}

function SparklesIcon() {
  return <Sparkles className="h-4 w-4" aria-hidden="true" />;
}

function normalizeSummaryMarkdown(value: string) {
  const text = value.trim();
  if (!text) return "";

  return text
    .replace(/([.!?])\s+([A-Z][A-Za-z0-9 /&(),–—-]{2,72}):\s+/g, "$1\n\n$2: ")
    .split(/\n{2,}/)
    .map((body) => body.trim())
    .filter(Boolean)
    .map((block) => {
      if (/^(#{1,6}\s|[-*+]\s|\d+\.\s|>\s|```|\|)/.test(block)) return block;

      const sectionMatch = block.match(/^([A-Z][A-Za-z0-9 /&(),–—-]{2,72}):\s+([\s\S]+)$/);
      if (!sectionMatch) return block;

      return `**${sectionMatch[1]}:** ${sectionMatch[2]}`;
    })
    .join("\n\n");
}

function toTitleCase(value: string) {
  const normalized = /^[a-z0-9_-]+$/.test(value) ? value.replace(/[-_]/g, " ") : value.replace(/_/g, " ");

  return normalized
    .split(" ")
    .map((word, index) => {
      if (!word) return word;
      const lower = word.toLowerCase();
      if (["and", "or", "of", "the", "in", "with", "to", "from", "for", "on", "at", "by"].includes(lower) && index !== 0) {
        return lower;
      }
      if (["ulab", "lmic", "lmics", "epr", "ieubk", "iq"].includes(lower)) return lower.toUpperCase();
      return word.replace(/[A-Za-z]+/g, (part) => {
        const partLower = part.toLowerCase();
        if (["and", "or", "of", "the", "in", "with", "to", "from", "for", "on", "at", "by"].includes(partLower) && index !== 0) {
          return partLower;
        }
        if (["ulab", "lmic", "lmics", "epr", "ieubk", "iq"].includes(partLower)) return partLower.toUpperCase();
        return `${part[0].toUpperCase()}${part.slice(1).toLowerCase()}`;
      });
    })
    .join(" ");
}

function formatTimestamp(value?: string | null) {
  if (!value) return "";
  const normalized = value.includes("T") ? value : `${value.replace(" ", "T")}Z`;
  const date = new Date(normalized);

  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat("en", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit"
  }).format(date);
}

function toEditable(paper: Paper): EditablePaper {
  return {
    id: paper.id,
    title: paper.title,
    authors: paper.authors,
    year: paper.year,
    publication: paper.publication,
    sourceType: paper.sourceType,
    doi: paper.doi,
    url: paper.url,
    abstract: paper.abstract,
    geography: paper.geography,
    geographyTags: paper.geographyTags,
    geographicScope: paper.geographicScope,
    countries: paper.countries,
    region: paper.region,
    topics: paper.topics,
    policyTags: paper.policyTags,
    populationTags: paper.populationTags,
    exposureTags: paper.exposureTags,
    evidenceType: paper.evidenceType,
    relevanceScore: paper.relevanceScore,
    reviewStatus: paper.reviewStatus,
    coreQuestion: paper.coreQuestion,
    keyFinding: paper.keyFinding,
    summary: paper.summary,
    decisionRelevance: paper.decisionRelevance,
    notes: paper.notes
  };
}

function toEditableDraft(paper?: Partial<EditablePaper> | null): EditablePaper {
  return {
    ...emptyPaper,
    ...(paper || {}),
    authors: paper?.authors || [],
    geographyTags: paper?.geographyTags || [],
    countries: paper?.countries || [],
    topics: paper?.topics || [],
    policyTags: paper?.policyTags || [],
    populationTags: paper?.populationTags || [],
    exposureTags: paper?.exposureTags || [],
    geographicScope: paper?.geographicScope || "unknown",
    region: paper?.region || ""
  };
}
